// src/utils/stundenAuszugUtils.js
// Helper functions for Stundenauszug (Hours Extract) feature

/**
 * Calculate total hours from duration hours and minutes
 * @param {Object} entry - Timekeeping entry
 * @returns {number} Total hours as decimal
 */
export const calculateHours = (entry) => {
  const hours = entry.durationHours || 0;
  const minutes = entry.durationMinutes || 0;
  return hours + minutes / 60;
};

/**
 * Get hourly rate for a timekeeping entry
 * Cascade: Internal phase rate → Internal default rate → Legacy (AbaNinja) phase → Legacy assignment → Team internal rate → 0
 * @param {Object} entry - Timekeeping entry
 * @param {Array} projectPhases - Array of project phases
 * @param {Array} projectAssignments - Array of project assignments (legacy AbaNinja)
 * @param {Array} products - Array of products from AbaNinja (legacy)
 * @param {Array} teamMembers - Array of team members
 * @param {Array} projectRates - Array of internal rates [{employeeId, phaseId, rate}] from projectRates table
 * @returns {number} Hourly rate in CHF
 */
export const getHourlyRate = (
  entry,
  projectPhases = [],
  projectAssignments = [],
  products = [],
  teamMembers = [],
  projectRates = []
) => {
  const personId = entry.employeeUuid;
  const entryDate = entry.date;

  if (!personId) return 0;

  // 1. Check internal project rates (new system — Ansätze tab)
  // Cascade: individual phase > global phase > individual default > global default
  if (projectRates && projectRates.length > 0) {
    // 1a. Phase-specific rates (individual, then global)
    if (entryDate && projectPhases && projectPhases.length > 0) {
      const activePhase = projectPhases.find((phase) => {
        if (!phase.startDate || !phase.endDate) return false;
        return entryDate >= phase.startDate && entryDate <= phase.endDate;
      });

      if (activePhase) {
        // Individual phase rate
        const individualPhaseRate = projectRates.find(
          (r) => r.employeeId === personId && r.phaseId === activePhase.id && r.rate > 0
        );
        if (individualPhaseRate) return individualPhaseRate.rate;

        // Global phase rate
        const globalPhaseRate = projectRates.find(
          (r) => r.employeeId === "_global" && r.phaseId === activePhase.id && r.rate > 0
        );
        if (globalPhaseRate) return globalPhaseRate.rate;
      }
    }

    // 1b. Project-level default (individual, then global)
    const individualDefault = projectRates.find(
      (r) => r.employeeId === personId && r.phaseId === "default" && r.rate > 0
    );
    if (individualDefault) return individualDefault.rate;

    const globalDefault = projectRates.find(
      (r) => r.employeeId === "_global" && r.phaseId === "default" && r.rate > 0
    );
    if (globalDefault) return globalDefault.rate;
  }

  // 2. Legacy: Check phase-specific rate via AbaNinja product
  if (entryDate && projectPhases && projectPhases.length > 0) {
    const activePhase = projectPhases.find((phase) => {
      if (!phase.startDate || !phase.endDate) return false;
      return entryDate >= phase.startDate && entryDate <= phase.endDate;
    });

    if (activePhase && activePhase.personProducts && activePhase.personProducts[personId]) {
      const productUuid = activePhase.personProducts[personId];
      const product = products.find((p) => p.uuid === productUuid);
      if (product && product.cost) {
        return product.cost;
      }
    }
  }

  // 3. Legacy: Check project-level assignment rate
  if (projectAssignments && projectAssignments.length > 0) {
    const assignment = projectAssignments.find(
      (a) => a.employeeUuid === personId
    );
    if (assignment && assignment.productUuid) {
      const product = products.find((p) => p.uuid === assignment.productUuid);
      if (product && product.cost) {
        return product.cost;
      }
    }
  }

  // 4. Fallback to internal rate from team member
  if (teamMembers && teamMembers.length > 0) {
    const teamMember = teamMembers.find(
      (tm) => tm.employeeUuid === personId,
    );
    if (teamMember && teamMember.internalRate) {
      return teamMember.internalRate;
    }
  }

  // 5. Default: 0
  return 0;
};

/**
 * Get internal hourly rate for a timekeeping entry (team member internal rate)
 * @param {Object} entry - Timekeeping entry
 * @param {Array} teamMembers - Array of team members
 * @returns {number} Internal hourly rate in CHF (0 if none defined)
 */
export const getInternalRate = (entry, teamMembers = []) => {
  const teamMember = teamMembers.find(
    (tm) => tm.employeeUuid === entry.employeeUuid
  );
  return teamMember?.internalRate || 0;
};

/**
 * Calculate amount (hours × rate)
 * @param {Object} entry - Timekeeping entry
 * @param {number} hourlyRate - Hourly rate in CHF
 * @returns {number} Amount in CHF
 */
export const calculateAmount = (entry, hourlyRate) => {
  const hours = calculateHours(entry);
  return hours * hourlyRate;
};

/**
 * Check if a timekeeping entry is invoiced
 * @param {Object} entry - Timekeeping entry
 * @param {Array} invoicedPeriods - Array of invoiced periods
 * @returns {boolean} True if entry is invoiced
 */
export const isEntryInvoiced = (entry, invoicedPeriods = []) => {
  if (!entry.date || !invoicedPeriods || invoicedPeriods.length === 0) {
    return false;
  }

  return invoicedPeriods.some((period) => {
    return entry.date >= period.dateFrom && entry.date <= period.dateTo;
  });
};

/**
 * Format CHF amount
 * @param {number} amount - Amount in CHF
 * @returns {string} Formatted string (e.g., "CHF 1'234.50")
 */
export const formatCHF = (amount) => {
  if (typeof amount !== "number" || isNaN(amount)) {
    return "CHF 0.00";
  }

  return new Intl.NumberFormat("de-CH", {
    style: "currency",
    currency: "CHF",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

/**
 * Format date in Swiss format (DD.MM.YYYY)
 * @param {string} dateStr - Date string in YYYY-MM-DD format
 * @returns {string} Formatted date (e.g., "31.12.2025")
 */
export const formatDateCH = (dateStr) => {
  if (!dateStr) return "";

  try {
    const [year, month, day] = dateStr.split("-");
    return `${day}.${month}.${year}`;
  } catch (error) {
    return dateStr;
  }
};

/**
 * Format datetime in Swiss format (DD.MM.YYYY HH:MM)
 * @param {string} isoDatetime - ISO datetime string
 * @returns {string} Formatted datetime
 */
export const formatDateTimeCH = (isoDatetime) => {
  if (!isoDatetime) return "";

  try {
    const date = new Date(isoDatetime);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");

    return `${day}.${month}.${year} ${hours}:${minutes}`;
  } catch (error) {
    return isoDatetime;
  }
};

/**
 * Sort entries by date and time (ascending)
 * @param {Array} entries - Array of timekeeping entries
 * @returns {Array} Sorted entries
 */
export const sortByDate = (entries) => {
  return [...entries].sort((a, b) => {
    const dateCompare = (a.date || "").localeCompare(b.date || "");
    if (dateCompare !== 0) return dateCompare;
    return (a.startTime || "").localeCompare(b.startTime || "");
  });
};

/**
 * Group entries by person with subtotals
 * @param {Array} entries - Array of timekeeping entries
 * @param {Array} projectPhases - Project phases for rate calculation
 * @param {Array} projectAssignments - Project assignments
 * @param {Array} products - Products array
 * @param {Array} teamMembers - Team members array
 * @param {Array} projectRates - Internal project rates
 * @param {string} rateMode - "extern" (project rate cascade) | "intern" (team member internal rate)
 * @returns {Object} Grouped entries with person names as keys
 */
export const groupByPerson = (
  entries,
  projectPhases = [],
  projectAssignments = [],
  products = [],
  teamMembers = [],
  projectRates = [],
  rateMode = "extern"
) => {
  const grouped = {};

  entries.forEach((entry) => {
    const personName = entry.employeeName || "Unbekannt";

    if (!grouped[personName]) {
      grouped[personName] = {
        entries: [],
        totalHours: 0,
        totalAmount: 0,
      };
    }

    const rate =
      rateMode === "intern"
        ? getInternalRate(entry, teamMembers)
        : getHourlyRate(
            entry,
            projectPhases,
            projectAssignments,
            products,
            teamMembers,
            projectRates
          );
    const hours = calculateHours(entry);
    const amount = calculateAmount(entry, rate);

    grouped[personName].entries.push({
      ...entry,
      rate,
      hours,
      amount,
    });
    grouped[personName].totalHours += hours;
    grouped[personName].totalAmount += amount;
  });

  return grouped;
};

/**
 * Calculate summary totals (Zwischensumme, MwSt, Total)
 * @param {Array} entries - Array of timekeeping entries
 * @param {Array} projectPhases - Project phases
 * @param {Array} projectAssignments - Project assignments
 * @param {Array} products - Products array
 * @param {Array} teamMembers - Team members array
 * @param {Array} invoicedPeriods - Invoiced periods (to exclude invoiced entries)
 * @param {Array} projectRates - Internal project rates
 * @param {string} rateMode - "extern" (project rate cascade) | "intern" (team member internal rate)
 * @returns {Object} Summary with zwischensumme, mwst, total
 */
export const calculateSummary = (
  entries,
  projectPhases = [],
  projectAssignments = [],
  products = [],
  teamMembers = [],
  invoicedPeriods = [],
  projectRates = [],
  rateMode = "extern"
) => {
  // Filter out invoiced entries for summary
  const nonInvoicedEntries = entries.filter(
    (entry) => !isEntryInvoiced(entry, invoicedPeriods)
  );

  const zwischensumme = nonInvoicedEntries.reduce((sum, entry) => {
    const rate =
      rateMode === "intern"
        ? getInternalRate(entry, teamMembers)
        : getHourlyRate(
            entry,
            projectPhases,
            projectAssignments,
            products,
            teamMembers,
            projectRates
          );
    const amount = calculateAmount(entry, rate);
    return sum + amount;
  }, 0);

  const mwst = zwischensumme * 0.081; // 8.1% VAT
  const total = zwischensumme + mwst;

  return {
    zwischensumme,
    mwst,
    total,
  };
};

/**
 * Filter entries by date range
 * @param {Array} entries - Array of timekeeping entries
 * @param {string} dateFrom - Start date (YYYY-MM-DD)
 * @param {string} dateTo - End date (YYYY-MM-DD)
 * @returns {Array} Filtered entries
 */
export const filterByDateRange = (entries, dateFrom, dateTo) => {
  if (!dateFrom && !dateTo) return entries;

  return entries.filter((entry) => {
    if (!entry.date) return false;

    if (dateFrom && entry.date < dateFrom) return false;
    if (dateTo && entry.date > dateTo) return false;

    return true;
  });
};

/**
 * Check if there are overlapping invoiced periods
 * @param {string} newDateFrom - New period start date
 * @param {string} newDateTo - New period end date
 * @param {Array} existingPeriods - Existing invoiced periods
 * @returns {Array} Array of overlapping periods
 */
export const findOverlappingPeriods = (
  newDateFrom,
  newDateTo,
  existingPeriods = []
) => {
  return existingPeriods.filter((period) => {
    // Periods overlap if: newStart <= existingEnd AND newEnd >= existingStart
    return newDateFrom <= period.dateTo && newDateTo >= period.dateFrom;
  });
};
